/**
 * Professional stats dashboard.
 *
 * Renders two ChartCards per profile:
 *   1. LineChart  — score per session, one line per activity
 *   2. BarChart   — duration per session, one bar group per date
 *
 * Activity toggle pills above the cards act as both filter and legend.
 * Each card can be flipped to reveal a data table.
 */
import { useState, useEffect, useMemo, type CSSProperties } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import { professional as api, type ProfessionalProfile, type ProfileStats } from '../api/client'
import PageHeader from '../components/PageHeader'
import LineChart, { type ChartSeries } from '../components/charts/LineChart'
import BarChart                        from '../components/charts/BarChart'
import ChartCard                       from '../components/charts/ChartCard'
import { ACTIVITY_COLORS, ACTIVITY_COLOR_DEFAULT } from '../components/charts/activityColors'

const ACTIVITY_EMOJI: Record<string, string> = {
  memory: '🃏', wordmatch: '🔤', cutlery: '🍴', wordsearch: '🔍',
  sudoku: '🔢', maths: '➕', shoppinglist: '🛒', eurmoney: '💶', pillbox: '💊',
}

interface Props {
  profiles: ProfessionalProfile[]
  onBack:   () => void
}

// ── Data helpers ───────────────────────────────────────────────

interface AggPoint { date: string; score: number | null; durationS: number | null }

/** Aggregate raw session points by date (average score + duration per day). */
function aggregatePoints(points: ProfileStats['evolution'][string]): AggPoint[] {
  const byDate = new Map<string, { scores: number[]; durMs: number[] }>()
  for (const pt of points) {
    if (!byDate.has(pt.date)) byDate.set(pt.date, { scores: [], durMs: [] })
    const e = byDate.get(pt.date)!
    if (pt.score     != null) e.scores.push(pt.score)
    if (pt.durationMs != null) e.durMs.push(pt.durationMs)
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, { scores, durMs }]) => ({
      date,
      score:     scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null,
      durationS: durMs.length  ? Math.round(durMs.reduce((a, b) => a + b, 0)  / durMs.length / 1000) : null,
    }))
}

const fmtDate = (iso: string) => {
  const [, m, d] = iso.split('-')
  return `${parseInt(d)}/${parseInt(m)}`
}

// ── Component ──────────────────────────────────────────────────

export default function ProfessionalStats({ profiles, onBack }: Props) {
  const { t } = useTranslation()
  const p = t.professional

  const [selectedProfileId, setSelectedProfileId] = useState(profiles[0]?.id ?? '')
  const [stats,   setStats]   = useState<ProfileStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeSet, setActiveSet] = useState<Set<string>>(new Set())

  // Fetch stats when profile changes
  useEffect(() => {
    if (!selectedProfileId) return
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
     
    setStats(null)
    api.getProfileStats(selectedProfileId)
      .then(data  => { if (!cancelled) setStats(data) })
      .catch(()   => { if (!cancelled) setStats(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [selectedProfileId])

  // When stats arrive, initialise the active set to all available activities
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (stats) setActiveSet(new Set(Object.keys(stats.evolution)))
  }, [stats])

  // Aggregate raw points → one row per date per activity
  const aggregated = useMemo<Record<string, AggPoint[]>>(() => {
    if (!stats) return {}
    return Object.fromEntries(
      Object.entries(stats.evolution).map(([slug, pts]) => [slug, aggregatePoints(pts)])
    )
  }, [stats])

  const allSlugs    = stats ? Object.keys(stats.evolution) : []
  const activeSlugs = allSlugs.filter(s => activeSet.has(s))

  // Union of all dates across active activities (sorted)
  const allDatesSet = new Set<string>()
  activeSlugs.forEach(slug => aggregated[slug]?.forEach(pt => allDatesSet.add(pt.date)))
  const allDates = [...allDatesSet].sort()

  const mkGameLabel = (slug: string) =>
    t.dashboard.games[slug as keyof typeof t.dashboard.games]?.label ?? slug

  const scoreSeries: ChartSeries[] = activeSlugs.map(slug => ({
    id:    slug,
    label: mkGameLabel(slug),
    color: ACTIVITY_COLORS[slug] ?? ACTIVITY_COLOR_DEFAULT,
    data:  allDates.map(date => ({
      x: date,
      y: aggregated[slug]?.find(pt => pt.date === date)?.score ?? null,
    })),
  }))

  const durationSeries: ChartSeries[] = activeSlugs.map(slug => ({
    id:    slug,
    label: mkGameLabel(slug),
    color: ACTIVITY_COLORS[slug] ?? ACTIVITY_COLOR_DEFAULT,
    data:  allDates.map(date => ({
      x: date,
      y: aggregated[slug]?.find(pt => pt.date === date)?.durationS ?? null,
    })),
  }))

  // Toggle an activity (keep at least one active)
  const toggleActivity = (slug: string) => {
    setActiveSet(prev => {
      const next = new Set(prev)
      if (next.has(slug)) {
        if (next.size > 1) next.delete(slug)
      } else {
        next.add(slug)
      }
      return next
    })
  }

  // ── Table views ──────────────────────────────────────────────

  const scoreTable = (
    <div style={ts.tableWrap}>
      <table style={ts.table}>
        <thead>
          <tr>
            <th style={ts.th}>{p.statsDate}</th>
            {scoreSeries.map(s => (
              <th key={s.id} style={{ ...ts.th, color: s.color }}>{s.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allDates.map((date, i) => (
            <tr key={date} style={i % 2 === 0 ? ts.rowEven : {}}>
              <td style={ts.td}>{fmtDate(date)}</td>
              {scoreSeries.map(s => {
                const val = s.data.find(d => d.x === date)?.y
                return <td key={s.id} style={ts.td}>{val != null ? val : '—'}</td>
              })}
            </tr>
          ))}
          {allDates.length === 0 && (
            <tr><td colSpan={scoreSeries.length + 1} style={ts.noDataCell}>{p.statsNoData}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )

  const durationTable = (
    <div style={ts.tableWrap}>
      <table style={ts.table}>
        <thead>
          <tr>
            <th style={ts.th}>{p.statsDate}</th>
            {durationSeries.map(s => (
              <th key={s.id} style={{ ...ts.th, color: s.color }}>{s.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allDates.map((date, i) => (
            <tr key={date} style={i % 2 === 0 ? ts.rowEven : {}}>
              <td style={ts.td}>{fmtDate(date)}</td>
              {durationSeries.map(s => {
                const val = s.data.find(d => d.x === date)?.y
                return <td key={s.id} style={ts.td}>{val != null ? `${val}s` : '—'}</td>
              })}
            </tr>
          ))}
          {allDates.length === 0 && (
            <tr><td colSpan={durationSeries.length + 1} style={ts.noDataCell}>{p.statsNoData}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )

  // ── Activity filter pills (serve as legend too) ──────────────

  const activityPills = allSlugs.length > 1 ? (
    <div style={s.pills}>
      <span style={s.pillsLabel}>{p.statsActivities}:</span>
      <div style={s.pillsRow}>
        {allSlugs.map(slug => {
          const isOn  = activeSet.has(slug)
          const color = ACTIVITY_COLORS[slug] ?? ACTIVITY_COLOR_DEFAULT
          const label = t.dashboard.games[slug as keyof typeof t.dashboard.games]?.label ?? slug
          const emoji = ACTIVITY_EMOJI[slug] ?? '🎮'
          return (
            <button
              key={slug}
              style={{
                ...s.pill,
                border:     `2px solid ${isOn ? color : '#e0d8d0'}`,
                background: isOn ? `${color}18` : '#f8f6f3',
                color:      isOn ? color : '#aaa',
              }}
              onClick={() => toggleActivity(slug)}
              aria-pressed={isOn}
            >
              <span style={{ fontSize: '0.9375rem' }}>{emoji}</span>
              {label}
            </button>
          )
        })}
      </div>
    </div>
  ) : null

  // ── Render ───────────────────────────────────────────────────

  return (
    <div style={s.root}>
      <PageHeader
        onBack={onBack}
        backLabel={p.back}
        title={profiles.length === 1 ? profiles[0].name : p.statsTitle}
      />
      <div style={s.inner}>

        {/* Profile selector — hidden when viewing a single profile */}
        {profiles.length > 1 && (
          <div style={s.filterRow}>
            <span style={s.filterLabel}>{p.statsFilter}:</span>
            <select
              style={s.select}
              value={selectedProfileId}
              onChange={e => setSelectedProfileId(e.target.value)}
            >
              {profiles.map(pr => (
                <option key={pr.id} value={pr.id}>{pr.name}</option>
              ))}
            </select>
          </div>
        )}

        {loading && <p style={s.loading}>…</p>}

        {!loading && stats && (
          <>
            {/* Summary chips */}
            <div style={s.summaryRow}>
              <div style={s.summaryCard}>
                <div style={s.summaryValue}>{stats.summary.totalSessions}</div>
                <div style={s.summaryLabel}>{p.statsTotalSessions}</div>
              </div>
              <div style={s.summaryCard}>
                <div style={s.summaryValue}>
                  {stats.summary.lastSessionAt ? fmtDate(stats.summary.lastSessionAt.slice(0, 10)) : '—'}
                </div>
                <div style={s.summaryLabel}>{p.statsLastSession}</div>
              </div>
            </div>

            {allSlugs.length === 0
              ? <p style={s.noData}>{p.statsNoData}</p>
              : (
                <>
                  {/* Activity filter / legend */}
                  {activityPills}

                  {/* Score line chart */}
                  <ChartCard
                    title={p.statsScoreChart}
                    chart={
                      <LineChart
                        series={scoreSeries}
                        formatX={fmtDate}
                        noData={p.statsNoData}
                      />
                    }
                    table={scoreTable}
                    showTableLabel={p.statsShowTable}
                    showChartLabel={p.statsShowChart}
                  />

                  {/* Duration bar chart */}
                  <ChartCard
                    title={p.statsDurationChart}
                    chart={
                      <BarChart
                        series={durationSeries}
                        formatX={fmtDate}
                        formatY={v => String(v)}
                        yUnit="s"
                        noData={p.statsNoData}
                      />
                    }
                    table={durationTable}
                    showTableLabel={p.statsShowTable}
                    showChartLabel={p.statsShowChart}
                  />
                </>
              )
            }
          </>
        )}

        {!loading && !stats && !loading && (
          <p style={s.noData}>{p.statsNoData}</p>
        )}

      </div>
    </div>
  )
}

// ── Styles ─────────────────────────────────────────────────────

const s: Record<string, CSSProperties> = {
  root: {
    minHeight:  '100vh',
    background: '#FAF6EF',
  },
  inner: {
    maxWidth:      '720px',
    margin:        '0 auto',
    padding:       '24px 16px 40px',
    display:       'flex',
    flexDirection: 'column',
    gap:           '20px',
  },
  filterRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        '10px',
  },
  filterLabel: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color:      '#555',
  },
  select: {
    fontSize: '1rem',
    padding:      '8px 12px',
    borderRadius: '10px',
    border:       '1px solid #ddd',
    background:   '#fff',
    minHeight:    '48px',
    cursor:       'pointer',
  },
  loading: {
    fontSize: '1.125rem',
    color:     '#888',
    textAlign: 'center',
    margin:    0,
  },
  summaryRow: {
    display: 'flex',
    gap:     '12px',
  },
  summaryCard: {
    flex:          1,
    background:    '#fff',
    borderRadius:  '16px',
    padding:       '20px',
    textAlign:     'center',
    border:        '1px solid #e8e0d5',
  },
  summaryValue: {
    fontSize: '2rem',
    fontWeight: 800,
    color:      '#2D3A2E',
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color:     '#888',
    marginTop: '4px',
  },
  noData: {
    fontSize: '1rem',
    color:     '#888',
    textAlign: 'center',
    padding:   '32px 0',
    margin:    0,
  },

  // Activity filter pills
  pills: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '8px',
  },
  pillsLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color:      '#888',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  pillsRow: {
    display:  'flex',
    flexWrap: 'wrap' as const,
    gap:      '8px',
  },
  pill: {
    display:      'flex',
    alignItems:   'center',
    gap:          '5px',
    padding:      '5px 12px',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight:   600,
    cursor:       'pointer',
    minHeight:    '36px',
    transition:   'all 0.15s',
  },
}

// Table styles (shared between score and duration tables)
const ts: Record<string, CSSProperties> = {
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width:          '100%',
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
  },
  th: {
    textAlign:    'left',
    padding:      '6px 10px',
    fontWeight:   700,
    fontSize: '0.875rem',
    borderBottom: '2px solid #f0ece6',
    whiteSpace:   'nowrap' as const,
  },
  td: {
    padding:    '7px 10px',
    color:      '#333',
    whiteSpace: 'nowrap' as const,
  },
  rowEven: {
    background: '#FAFAF8',
  },
  noDataCell: {
    textAlign: 'center',
    color:     '#bbb',
    padding:   '24px',
    fontSize: '0.9375rem',
  },
}
